import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  ThemeProvider,
  Typography,
  Checkbox,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { datadogRum } from '@datadog/browser-rum';

import { AccountIntent, useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';

import {
  ContentContainer,
  FlexContainer,
  HeaderBox,
  Title,
  PhoneLabel,
  RequiredConsentLabel,
  OptionalConsentLabel,
  DividerContainer,
  DividerText,
  continueButtonStyles,
} from './AuthMethodSelection.styles';

import { activateSleepioPhone } from '@/utils/sleepioActivationApi';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { logError } from '@/utils/logError';
import { buildRedirectParam } from '@/utils/urlHelpers';

import sleepioTheme from '@/themes/sleepioTheme';
import Button from '@/components/Button';
import SocialButton from '@/components/SocialButton/SocialButton';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { useConsent } from '@/hooks/useConsent';
import EmailIcon from '@/assets/Email.svg';

const PHONE_ERROR_KEY = 'coverage.sleepio.patient.please_enter_valid_phone_number';

// Create Account: phone + all three consent checkboxes required/optional
const createSchema = z.object({
  phoneNumber: z.string().refine(
    (v) => isValidPhoneNumber(v || ''),
    { message: PHONE_ERROR_KEY },
  ),
  readAndAcceptTerms: z.boolean().refine((v) => v === true),
  agreeToReceiveTextMessages: z.boolean().refine((v) => v === true),
  marketingConsent: z.boolean(),
});

// Sign In: phone only, no consent required
const loginSchema = z.object({
  phoneNumber: z.string().refine(
    (v) => isValidPhoneNumber(v || ''),
    { message: PHONE_ERROR_KEY },
  ),
});

type FormValues = z.infer<typeof createSchema>;

const AuthMethodSelectionInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const { t } = useTranslation();
  const { state, dispatch } = useActivation();
  const history = useHistory();
  const base = useActivationBase();
  const isCreate = state.accountIntent === AccountIntent.Create;

  const posthog = usePostHog();

  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const resolver = isCreate
    ? zodResolver(createSchema)
    : zodResolver(loginSchema);

  const methods = useForm<FormValues>({
    resolver,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '',
      readAndAcceptTerms: false,
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
    },
  });
  const { handleSubmit, control, formState } = methods;

  const onSubmit = async (data: FormValues) => {
    setIsSending(true);
    setSendError('');
    try {
      const phDistinctId = posthog?.get_distinct_id();
      // TEMP debug logging (CAN-1268) — remove once login stitching is confirmed working.
      datadogRum.addAction('[AuthMethodSelection] sending phone activation', {
        isCreate,
        phDistinctId,
      });
      await activateSleepioPhone({
        phone: data.phoneNumber,
        organizationSlug: state.orgSlug,
        phDistinctId,
      });
      const { error: otpError } = await getSupabaseClient().auth.signInWithOtp({
        phone: data.phoneNumber,
        options: { data: { platform: 'sleepio' } },
      });
      if (otpError) {
        logError('[AuthMethodSelection]', 'signInWithOtp failed', otpError);
        setSendError(t('coverage.pre_onboarding.sleepio.activation.error_send_failed'));
        setIsSending(false);
        return;
      }
      // Keep isSending true — component unmounts on navigation, no need to reset.
      dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'phone' });
      dispatch({ type: 'SET_PHONE', phone: data.phoneNumber });
      dispatch({ type: 'SET_STEP', step: 'otp_verification' });
      history.push(`${base}/otp${buildRedirectParam(state.redirectTo)}`);
    } catch (err) {
      logError('[AuthMethodSelection]', 'activateSleepioPhone failed', err);
      setSendError(t('coverage.pre_onboarding.sleepio.activation.error_send_failed'));
      setIsSending(false);
    }
  };

  const handleEmailClick = () => {
    dispatch({ type: 'SET_AUTH_METHOD', authMethod: 'email' });
    dispatch({ type: 'SET_STEP', step: 'email_entry' });
    history.push(`${base}/email${buildRedirectParam(state.redirectTo)}`);
  };

  // accountIntent is always set from the route path — never null (see ActivationRouter).
  const titleKey = isCreate
    ? 'coverage.pre_onboarding.sleepio.activation.create_setup.title'
    : 'coverage.pre_onboarding.sleepio.activation.login_setup.title';

  const subTitleKey = isCreate
    ? 'coverage.pre_onboarding.sleepio.activation.create_setup.sub_title'
    : 'coverage.pre_onboarding.sleepio.activation.login_setup.sub_title';

  const continueKey = isCreate
    ? 'coverage.pre_onboarding.sleepio.activation.create_setup.continue'
    : 'coverage.pre_onboarding.sleepio.activation.login_setup.login';

  const sendingKey = 'coverage.pre_onboarding.sleepio.activation.sending';

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        <HeaderBox style={isCreate ? undefined : { marginBottom: 24 }}>
          <Title variant="h2" color="textPrimary">
            {t(titleKey)}
          </Title>
          <Typography color="textPrimary">
            {t(subTitleKey)}
          </Typography>
        </HeaderBox>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <PhoneLabel variant="body1">
              {t('coverage.pre_onboarding.sleepio.activation.phone_label')}
            </PhoneLabel>

            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: t(PHONE_ERROR_KEY),
                validate: (v) => isValidPhoneNumber(v || '') || t(PHONE_ERROR_KEY),
              }}
              render={({ field, fieldState }) => (
                <PhoneNumberInput
                  id="phoneNumber"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('coverage.sleepio.patient.phone_label')}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? t(fieldState.error.message ?? '') : ''}
                  required
                />
              )}
            />

            {isCreate && (
              <>
                <Controller
                  name="readAndAcceptTerms"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <RequiredConsentLabel
                      label={(
                        <Typography variant="caption">
                          {t('coverage.pre_onboarding.sleepio.activation.create_setup.read_and_accept_terms')}
                          *
                        </Typography>
                      )}
                      control={(
                        <Checkbox
                          {...field}
                          checked={Boolean(field.value)}
                          data-testid="consent-terms-checkbox"
                        />
                      )}
                    />
                  )}
                />

                <Controller
                  name="agreeToReceiveTextMessages"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <RequiredConsentLabel
                      label={(
                        <Typography variant="caption">
                          {t('coverage.pre_onboarding.sleepio.activation.create_setup.agree_to_receive_text_messages')}
                          *
                        </Typography>
                      )}
                      control={(
                        <Checkbox
                          {...field}
                          checked={Boolean(field.value)}
                          data-testid="consent-required-checkbox"
                        />
                      )}
                    />
                  )}
                />

                <Controller
                  name="marketingConsent"
                  control={control}
                  render={({ field }) => (
                    <OptionalConsentLabel
                      label={(
                        <Typography variant="caption">
                          {t('coverage.pre_onboarding.sleepio.activation.create_setup.marketing_consent')}
                        </Typography>
                      )}
                      control={(
                        <Checkbox
                          {...field}
                          checked={Boolean(field.value)}
                          data-testid="consent-optional-checkbox"
                        />
                      )}
                    />
                  )}
                />
              </>
            )}

            <Button
              variant="contained"
              data-testid="continue-button"
              type="submit"
              fullWidth
              color="primary"
              disabled={!formState.isValid || isSending}
              rounded
              style={isCreate ? continueButtonStyles : { ...continueButtonStyles, marginTop: 24 }}
            >
              {isSending ? (
                <>
                  <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                  {t(sendingKey)}
                </>
              ) : t(continueKey)}
            </Button>

            {sendError && (
              <Typography color="error" data-testid="send-error-message" style={{ marginTop: 8 }}>
                {sendError}
              </Typography>
            )}

            <DividerContainer>
              <Divider />
              <DividerText>
                {t('coverage.pre_onboarding.sleepio.activation.or')}
              </DividerText>
            </DividerContainer>

            <SocialButton
              icon={<img src={EmailIcon} alt="Email icon" width={20} height={20} />}
              labelKey="coverage.pre_onboarding.sleepio.activation.continue_email"
              testId="continue-email-button"
              onClick={handleEmailClick}
              marginBottom={0}
            />
          </form>
        </FormProvider>

        <ConsentWidget />
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export const AuthMethodSelection: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <AuthMethodSelectionInner />
  </ThemeProvider>
);
